import { useId, type Ref } from 'react'
import {
  MOT_VEHICLE_HINT,
  MOT_VEHICLE_OPTIONS,
  MOT_VEHICLE_QUESTION,
} from '../lib/motVehicleType'
import type { WidgetMotVehicleAnswer } from '../types/PublicBookingChannelTypes'

export interface MotVehicleTypeQuestionProps {
  /** Null until the customer answers: nothing is chosen for them. */
  value: WidgetMotVehicleAnswer | null
  onChange: (value: WidgetMotVehicleAnswer) => void
  /** True while the new answer is being priced. */
  busy: boolean
  error: string | null
  /** The group, so the widget can bring the customer back to it. */
  fieldsetRef?: Ref<HTMLFieldSetElement>
}

/**
 * "What is it?" (ACA-110): one question, asked only when the lookup could not
 * tell the MOT class, with nothing chosen. Native radios, so a keyboard or a
 * screen reader gets the group and its arrow keys for free.
 */
export function MotVehicleTypeQuestion({
  value,
  onChange,
  busy,
  error,
  fieldsetRef,
}: MotVehicleTypeQuestionProps) {
  const name = useId()
  const hintId = `${name}-hint`
  const errorId = `${name}-error`

  return (
    <fieldset
      ref={fieldsetRef}
      className="ac-question"
      aria-describedby={error ? `${hintId} ${errorId}` : hintId}
      aria-busy={busy}
    >
      <legend className="ac-subheading">{MOT_VEHICLE_QUESTION}</legend>
      <p id={hintId} className="ac-hint">
        {MOT_VEHICLE_HINT}
      </p>
      <div className="ac-choices">
        {MOT_VEHICLE_OPTIONS.map((option) => {
          const checked = option.value === value
          return (
            <label
              key={option.value}
              className={`ac-choice${checked ? ' ac-choice--selected' : ''}`}
            >
              <input
                type="radio"
                className="ac-sr-only"
                name={name}
                value={option.value}
                checked={checked}
                required
                onChange={() => onChange(option.value)}
              />
              <span className="ac-choice__dot" aria-hidden="true" />
              <span className="ac-choice__label">{option.label}</span>
            </label>
          )
        })}
      </div>
      {error ? (
        <p id={errorId} className="ac-field-error" role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  )
}
